import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { settingsAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Code, Globe, Mail, Phone, Save, Info, Search, FileText, ExternalLink, Trash2, RefreshCw, MapPin, CheckCircle2, AlertTriangle } from "lucide-react";
import axios from "axios";
import ImageUpload from "@/components/ImageUpload";

const AdminSettings = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [updatingSitemap, setUpdatingSitemap] = useState(false);
  const [sitemapStats, setSitemapStats] = useState(null);
  const [regeneratingMeta, setRegeneratingMeta] = useState(false);
  const [metaStats, setMetaStats] = useState(null);
  
  const [settings, setSettings] = useState({
    header_code: "",
    footer_code: "",
    site_name: "Kokan Homestays",
    site_description: "Find your perfect homestay",
    contact_email: "",
    contact_phone: "",
    // SEO Settings
    robots_txt: `User-agent: *
Allow: /
Disallow: /admin/

Sitemap: {BASE_URL}/sitemap.xml`,
    home_meta_title: "",
    home_meta_description: "",
    home_og_image: "",
    default_og_image: "",
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings({
        header_code: response.data.header_code || "",
        footer_code: response.data.footer_code || "",
        site_name: response.data.site_name || "Kokan Homestays",
        site_description: response.data.site_description || "",
        contact_email: response.data.contact_email || "",
        contact_phone: response.data.contact_phone || "",
        robots_txt: response.data.robots_txt || `User-agent: *
Allow: /
Disallow: /admin/

Sitemap: {BASE_URL}/sitemap.xml`,
        home_meta_title: response.data.home_meta_title || "",
        home_meta_description: response.data.home_meta_description || "",
        home_og_image: response.data.home_og_image || "",
        default_og_image: response.data.default_og_image || "",
      });
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(settings, token);
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleClearCache = async () => {
    setClearingCache(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/cache/clear`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success("Cache cleared successfully!");
      } else {
        toast.error("Failed to clear cache");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear cache");
    } finally {
      setClearingCache(false);
    }
  };

  const handleUpdateSitemap = async () => {
    setUpdatingSitemap(true);
    setSitemapStats(null);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/sitemap/regenerate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSitemapStats(response.data.stats);
        toast.success(`Sitemap updated! ${response.data.stats.total_urls} URLs indexed.`);
      } else {
        toast.error("Failed to update sitemap");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update sitemap");
    } finally {
      setUpdatingSitemap(false);
    }
  };

  const handleRegenerateMeta = async () => {
    setRegeneratingMeta(true);
    setMetaStats(null);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/seo/regenerate-meta-descriptions`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setMetaStats(response.data);
        toast.success(`Meta descriptions updated! ${response.data.updated_count} listings updated.`);
      } else {
        toast.error("Failed to regenerate meta descriptions");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to regenerate meta descriptions");
    } finally {
      setRegeneratingMeta(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-settings">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-foreground mb-2">
              Site Settings
            </h1>
            <p className="text-muted-foreground">Configure your site settings, SEO, analytics, and tracking codes</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="rounded-full" data-testid="save-settings-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Settings
          </Button>
        </div>

        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="bg-white shadow-soft rounded-xl p-1">
            <TabsTrigger value="general" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
              <Globe className="w-4 h-4 mr-2" />
              General
            </TabsTrigger>
            <TabsTrigger value="seo" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
              <Search className="w-4 h-4 mr-2" />
              SEO
            </TabsTrigger>
            <TabsTrigger value="analytics" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
              <Code className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="system" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white">
              <RefreshCw className="w-4 h-4 mr-2" />
              System
            </TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general" className="space-y-6">
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Site Info Section */}
              <div className="bg-white rounded-xl p-6 shadow-soft">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Site Information</h2>
                    <p className="text-sm text-muted-foreground">Basic site details</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Site Name</label>
                    <Input
                      value={settings.site_name}
                      onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                      placeholder="Kokan Homestays"
                      data-testid="site-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Site Description</label>
                    <Textarea
                      value={settings.site_description}
                      onChange={(e) => setSettings({ ...settings, site_description: e.target.value })}
                      placeholder="Find your perfect homestay"
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Contact Info Section */}
              <div className="bg-white rounded-xl p-6 shadow-soft">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Contact Information</h2>
                    <p className="text-sm text-muted-foreground">Public contact details</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Contact Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="email"
                        value={settings.contact_email}
                        onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                        placeholder="contact@example.com"
                        className="pl-10"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Contact Phone</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="tel"
                        value={settings.contact_phone}
                        onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                        placeholder="+91 98765 43210"
                        className="pl-10"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* SEO Tab */}
          <TabsContent value="seo" className="space-y-6">
            {/* Quick Links */}
            <div className="bg-white rounded-xl p-4 shadow-soft flex flex-wrap gap-4 items-center">
              <span className="text-sm font-medium text-foreground">Quick Links:</span>
              <a 
                href="https://kokanhomestays.in/sitemap.xml" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                <FileText className="w-4 h-4" />
                View Sitemap.xml
                <ExternalLink className="w-3 h-3" />
              </a>
              <a 
                href="https://kokanhomestays.in/robots.txt" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                <FileText className="w-4 h-4" />
                View robots.txt
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              {/* Home Page Meta Tags */}
              <div className="bg-white rounded-xl p-6 shadow-soft lg:col-span-2">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Search className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Home Page SEO</h2>
                    <p className="text-sm text-muted-foreground">Meta tags for your home page</p>
                  </div>
                </div>
                
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Meta Title</label>
                    <Input
                      value={settings.home_meta_title}
                      onChange={(e) => setSettings({ ...settings, home_meta_title: e.target.value })}
                      placeholder="Kokan Homestays - Find Your Perfect Homestay in Konkan"
                      data-testid="home-meta-title"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Recommended: 50-60 characters</p>
                  </div>
                  <div>
                    <ImageUpload
                      value={settings.home_og_image}
                      onChange={(url) => setSettings({ ...settings, home_og_image: url })}
                      token={token}
                      label="OG Image"
                      placeholder="https://example.com/og-image.jpg"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Image for social media sharing (1200x630px)</p>
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium mb-1 block">Meta Description</label>
                    <Textarea
                      value={settings.home_meta_description}
                      onChange={(e) => setSettings({ ...settings, home_meta_description: e.target.value })}
                      placeholder="Discover unique homestays across India. Book authentic local experiences with verified hosts, home-cooked meals, and prime locations."
                      rows={3}
                      data-testid="home-meta-description"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Recommended: 150-160 characters</p>
                  </div>
                </div>
              </div>

              {/* Default OG Image */}
              <div className="bg-white rounded-xl p-6 shadow-soft">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Default OG Image</h2>
                    <p className="text-sm text-muted-foreground">Fallback image for pages without specific OG image</p>
                  </div>
                </div>
                
                <div>
                  <ImageUpload
                    value={settings.default_og_image}
                    onChange={(url) => setSettings({ ...settings, default_og_image: url })}
                    token={token}
                    label="Default OG Image URL"
                    placeholder="https://example.com/default-og.jpg"
                  />
                </div>
              </div>

              {/* Robots.txt */}
              <div className="bg-white rounded-xl p-6 shadow-soft">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">robots.txt</h2>
                    <p className="text-sm text-muted-foreground">Control search engine crawlers</p>
                  </div>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <p className="text-xs text-amber-800">
                    <strong>Tip:</strong> Use <code className="bg-amber-100 px-1 rounded">{"{BASE_URL}"}</code> as a placeholder - it will be replaced with your actual domain.
                  </p>
                </div>
                
                <Textarea
                  value={settings.robots_txt}
                  onChange={(e) => setSettings({ ...settings, robots_txt: e.target.value })}
                  placeholder={`User-agent: *
Allow: /
Disallow: /admin/

Sitemap: {BASE_URL}/sitemap.xml`}
                  rows={8}
                  className="font-mono text-sm"
                  data-testid="robots-txt-input"
                />
              </div>
            </div>

            {/* Sitemap Info */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-green-800">
                  <p className="font-medium mb-1">Sitemap is Auto-Generated</p>
                  <p>Your sitemap at <code className="bg-green-100 px-1 rounded">/sitemap.xml</code> is automatically generated and includes:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1 text-green-700">
                    <li>All published homestay pages (draft/unpublished excluded)</li>
                    <li>Active location pages (Kokan region only)</li>
                    <li>All published blog posts</li>
                    <li>Static pages (Home, Listings, Places, Blog)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Manual Sitemap Update */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Manual Sitemap Update</h2>
                  <p className="text-sm text-muted-foreground">Force regenerate the sitemap with latest data</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    <strong>When to use:</strong> After bulk changes, deleting listings, or if you notice old/invalid URLs in your sitemap.
                    This performs a full rebuild (no cache).
                  </p>
                </div>
                
                <Button 
                  onClick={handleUpdateSitemap} 
                  disabled={updatingSitemap}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  data-testid="update-sitemap-btn"
                >
                  {updatingSitemap ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  {updatingSitemap ? "Regenerating Sitemap..." : "Update Sitemap Now"}
                </Button>

                {/* Sitemap Stats */}
                {sitemapStats && (
                  <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-800">Sitemap Regenerated Successfully</span>
                      </div>
                      <a 
                        href="https://kokanhomestays.in/sitemap.xml" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                      >
                        View Live Sitemap <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="bg-white p-3 rounded border text-center">
                        <p className="text-2xl font-bold text-green-600">{sitemapStats.total_urls}</p>
                        <p className="text-xs text-muted-foreground">Total URLs</p>
                      </div>
                      <div className="bg-white p-3 rounded border text-center">
                        <p className="text-2xl font-bold text-blue-600">{sitemapStats.homestays}</p>
                        <p className="text-xs text-muted-foreground">Homestays</p>
                      </div>
                      <div className="bg-white p-3 rounded border text-center">
                        <p className="text-2xl font-bold text-purple-600">{sitemapStats.locations}</p>
                        <p className="text-xs text-muted-foreground">Locations</p>
                      </div>
                      <div className="bg-white p-3 rounded border text-center">
                        <p className="text-lg font-semibold text-gray-700">{sitemapStats.blogs}</p>
                        <p className="text-xs text-muted-foreground">Blog Posts</p>
                      </div>
                      <div className="bg-white p-3 rounded border text-center">
                        <p className="text-lg font-semibold text-gray-700">{sitemapStats.search_pages}</p>
                        <p className="text-xs text-muted-foreground">Search Pages</p>
                      </div>
                      <div className="bg-white p-3 rounded border text-center">
                        <p className="text-lg font-semibold text-gray-700">{sitemapStats.static_pages}</p>
                        <p className="text-xs text-muted-foreground">Static Pages</p>
                      </div>
                    </div>
                    {sitemapStats.excluded_count > 0 && (
                      <div className="mt-3 flex items-center gap-2 text-amber-700 bg-amber-50 p-2 rounded">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-sm">
                          {sitemapStats.excluded_count} URLs excluded (drafts, redirects, or non-Kokan locations)
                        </span>
                      </div>
                    )}
                    <div className="mt-3 pt-3 border-t flex items-center justify-between">
                      <p className="text-xs text-muted-foreground">
                        Generated: {new Date(sitemapStats.timestamp).toLocaleString()}
                      </p>
                      <p className="text-xs text-green-600 font-medium">
                        ✓ Changes visible immediately (no caching)
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Regenerate Meta Descriptions */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Regenerate Meta Descriptions</h2>
                  <p className="text-sm text-muted-foreground">Update all listing meta descriptions with pricing</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>What this does:</strong> Updates all homestay meta descriptions to include pricing (if available).
                    <br />
                    <span className="text-blue-600">Template: &quot;Book [Name] in [Location]. Comfortable stay with modern amenities. Prices from ₹[Price]/night. Book now.&quot;</span>
                  </p>
                </div>
                
                <Button 
                  onClick={handleRegenerateMeta} 
                  disabled={regeneratingMeta}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="regenerate-meta-btn"
                >
                  {regeneratingMeta ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  {regeneratingMeta ? "Regenerating..." : "Regenerate All Meta Descriptions"}
                </Button>

                {/* Meta Stats */}
                {metaStats && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">Meta Descriptions Updated</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="bg-white p-2 rounded border">
                        <span className="text-muted-foreground">Total:</span>
                        <span className="ml-2 font-semibold text-foreground">{metaStats.total_homestays}</span>
                      </div>
                      <div className="bg-white p-2 rounded border">
                        <span className="text-muted-foreground">Updated:</span>
                        <span className="ml-2 font-semibold text-green-600">{metaStats.updated_count}</span>
                      </div>
                      <div className="bg-white p-2 rounded border">
                        <span className="text-muted-foreground">Unchanged:</span>
                        <span className="ml-2 font-semibold text-foreground">{metaStats.skipped_count}</span>
                      </div>
                    </div>
                    {metaStats.details && metaStats.details.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-muted-foreground mb-2">Sample updates:</p>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {metaStats.details.map((item, idx) => (
                            <div key={idx} className="text-xs bg-white p-2 rounded border">
                              <span className="font-medium">{item.name}</span>
                              {item.price && <span className="text-emerald-600 ml-2">₹{item.price}/night</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {/* Header Code Section */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <Code className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Header Code (Analytics & Tracking)</h2>
                  <p className="text-sm text-muted-foreground">Add Google Analytics, Search Console, or other tracking codes</p>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex gap-2">
                  <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">Paste your tracking codes here:</p>
                    <ul className="list-disc list-inside space-y-1 text-amber-700">
                      <li>Google Analytics (GA4): <code className="bg-amber-100 px-1 rounded">&lt;script&gt;...gtag...&lt;/script&gt;</code></li>
                      <li>Google Search Console: <code className="bg-amber-100 px-1 rounded">&lt;meta name=&quot;google-site-verification&quot; ...&gt;</code></li>
                      <li>Facebook Pixel, Hotjar, or any other head scripts</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Textarea
                value={settings.header_code}
                onChange={(e) => setSettings({ ...settings, header_code: e.target.value })}
                placeholder={`<!-- Google Analytics -->
<script async src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config', 'G-XXXXXXXXXX');
</script>

<!-- Google Search Console Verification -->
<meta name="google-site-verification" content="your-verification-code" />`}
                rows={12}
                className="font-mono text-sm"
                data-testid="header-code-input"
              />
            </div>

            {/* Footer Code Section */}
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Code className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Footer Code (Optional)</h2>
                  <p className="text-sm text-muted-foreground">Scripts to load before closing &lt;/body&gt; tag</p>
                </div>
              </div>
              
              <Textarea
                value={settings.footer_code}
                onChange={(e) => setSettings({ ...settings, footer_code: e.target.value })}
                placeholder={`<!-- Chat widget, conversion tracking, etc. -->
<script>
  // Your footer scripts here
</script>`}
                rows={6}
                className="font-mono text-sm"
                data-testid="footer-code-input"
              />
            </div>
          </TabsContent>

          {/* System Tab */}
          <TabsContent value="system" className="space-y-6">
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Cache Management */}
              <div className="bg-white rounded-xl p-6 shadow-soft">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Cache Management</h2>
                    <p className="text-sm text-muted-foreground">Clear application cache</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    If you&apos;re experiencing stale data or display issues, clearing the cache can help. 
                    This will refresh all data connections and force the system to fetch fresh data.
                  </p>
                  
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> This action clears server-side cache. Users may need to refresh their browser 
                      to see the latest changes (Ctrl+F5 or Cmd+Shift+R).
                    </p>
                  </div>
                  
                  <Button 
                    onClick={handleClearCache} 
                    disabled={clearingCache}
                    variant="destructive"
                    className="w-full"
                    data-testid="clear-cache-btn"
                  >
                    {clearingCache ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Clear Cache
                  </Button>
                </div>
              </div>

              {/* System Info */}
              <div className="bg-white rounded-xl p-6 shadow-soft">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Info className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">System Information</h2>
                    <p className="text-sm text-muted-foreground">Quick links and info</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-muted-foreground">Robots.txt</span>
                    <a 
                      href="https://kokanhomestays.in/robots.txt" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                    >
                      View <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-muted-foreground">Sitemap.xml</span>
                    <a 
                      href="https://kokanhomestays.in/sitemap.xml" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1"
                    >
                      View <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-muted-foreground">Admin Login URL</span>
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded">/admin/login</code>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Save Button (mobile) */}
        <div className="lg:hidden">
          <Button onClick={handleSave} disabled={saving} className="w-full rounded-full" size="lg">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Settings
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
